import React, { useCallback, useEffect, useState } from 'react';
import {
  getBillingStartPreview,
  resendBillingStartNotice,
  startChannelBilling,
} from '../../api/system/system.api';

/**
 * 고객사 상세 '기본 정보' 의 **과금 시작** 구역(2026-10-19).
 *
 * ## 규칙 (사용자 결정, `_ai-runs/2026-10-15_004_billing_invoice_month_scope/007`)
 *  - 과금 기준일(`true_start_at`)은 **담당자가 이 버튼을 눌러야만** 고정된다. 가입이 몇 번 일어나도 자동으로 넘어가지 않는다 —
 *    세팅을 고객과 주고받는 도중에 과금 안내가 나가는 사고를 막기 위해서다.
 *  - 시작일 = 누른 날의 **다음 날**(KST). 고객 Owner 휴대폰으로 안내 LMS 가 나간다(🔴 실제 발송).
 *  - `pseudo_first_use_at`(4번째 이벤트 가입 시각)은 **참고용**으로만 보여 준다.
 *
 * 서버가 내려주는 `blocking`(시작 자체를 막는 사유)과 `noticeProblems`(문자를 못 보내는 사유)를 그대로 그린다.
 * 후자는 '안내 없이 시작' 을 고르면 넘어갈 수 있다 — 휴대폰이 없거나 야간이어도 시작일은 고정해야 할 때가 있다.
 *
 * api 는 실패를 **예외**로 던진다(`settlementResult`) — 실패를 빈 값으로 바꾸면 "시작됐다" 로 오독한다.
 */
export default function BillingStartPanel({ channelId, onChanged }) {
  const [preview, setPreview] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [open, setOpen] = useState(false);
  const [skipNotice, setSkipNotice] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoadErr('');
    try {
      const { data } = await getBillingStartPreview(channelId);
      setPreview(data ?? null);
    } catch (e) {
      setPreview(null);
      setLoadErr(e?.message ?? '과금 시작 정보를 불러오지 못했습니다.');
    }
  }, [channelId]);

  // 채널이 바뀌면 앞 채널의 확인 패널·결과가 남지 않게 전부 비운다(다른 고객사의 문구를 보고 누르면 안 된다).
  useEffect(() => {
    setOpen(false);
    setSkipNotice(false);
    setResult(null);
    setErr('');
    setPreview(null);
    if (channelId) load();
  }, [channelId, load]);

  const canStart = preview && preview.blocking.length === 0 && (skipNotice || preview.noticeProblems.length === 0);

  const start = async () => {
    if (!preview || !canStart || busy) return;
    const lines = [
      `'${preview.channelName}' 과금을 시작합니다.`,
      '',
      `과금 시작일: ${preview.startDate} (내일부터)`,
      skipNotice
        ? '안내 문자: 보내지 않음 (담당자가 따로 안내)'
        : `안내 문자(LMS): ${preview.phoneMasked ?? '-'} 로 실제 발송됩니다.`,
      '',
      '시작일은 되돌릴 수 없습니다. 계속할까요?',
    ];
    if (!window.confirm(lines.join('\n'))) return;
    setBusy(true);
    setErr('');
    try {
      const { data } = await startChannelBilling(channelId, { skipNotice });
      setResult(data);
      setOpen(false);
      await load();
      onChanged?.();
    } catch (e) {
      setErr(e?.message ?? '과금 시작에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (busy) return;
    if (!window.confirm(`안내 문자를 다시 보냅니다.\n${preview?.phoneMasked ?? '-'} 로 실제 발송됩니다. 계속할까요?`)) return;
    setBusy(true);
    setErr('');
    try {
      const { data } = await resendBillingStartNotice(channelId);
      setResult(data);
    } catch (e) {
      setErr(e?.message ?? '재발송에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  };

  if (loadErr) return <div className="text-sm text-red-600">과금 시작: {loadErr}</div>;
  if (!preview) return <div className="text-sm text-gray-400">과금 시작 정보 확인 중…</div>;

  const pseudo = (
    <span className="text-xs text-gray-400" title="4번째 이벤트 회원가입 시각(1~3번째는 세팅 중 테스트로 봄). 참고용이며 과금과 무관합니다.">
      첫 이용(참고) {preview.pseudoFirstUseAt ?? '—'}
    </span>
  );

  const resultLine = result && (
    <span className={`text-xs ${result.noticeSent ? 'text-green-700' : 'text-amber-700'}`}>
      {result.noticeSent
        ? `안내 문자 접수 완료 (${result.phoneMasked ?? ''})`
        : `안내 문자 미발송${result.noticeMessage ? ` — ${result.noticeMessage}` : ''}${result.noticeCode ? ` [${result.noticeCode}]` : ''}`}
    </span>
  );

  if (preview.trueStartAt) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <div>
          <span className="text-gray-500">과금 시작일 </span>
          <b className="text-gray-900">{preview.trueStartAt}</b>
          {(preview.trueStartSetBy || preview.trueStartSetAt) && (
            <span className="ml-1 text-xs text-gray-400">({preview.trueStartSetBy ?? '-'} · {preview.trueStartSetAt ?? '-'})</span>
          )}
        </div>
        {pseudo}
        <button
          className="px-2 py-0.5 rounded text-xs border bg-white text-gray-700 border-gray-300 disabled:opacity-50"
          onClick={resend}
          disabled={busy || preview.noticeProblems.length > 0}
          title={preview.noticeProblems.length > 0 ? preview.noticeProblems.join(', ') : '같은 안내 문구를 다시 보냅니다(실제 발송)'}
        >
          안내 문자 다시 보내기
        </button>
        {resultLine}
        {err && <span className="text-xs text-red-600">{err}</span>}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <div>
          <span className="text-gray-500">과금 시작일 </span>
          <span className="text-amber-700 font-semibold">시작 전</span>
        </div>
        {pseudo}
        <button
          className="px-2 py-0.5 rounded text-xs border bg-white text-indigo-700 border-indigo-300 disabled:opacity-50"
          onClick={() => setOpen((v) => !v)}
          disabled={busy}
        >
          {open ? '닫기' : '과금 시작…'}
        </button>
        {resultLine}
        {err && <span className="text-xs text-red-600">{err}</span>}
      </div>

      {open && (
        <div className="mt-1 border rounded bg-amber-50 p-3 text-sm space-y-2">
          <div>
            과금 시작일 <b>{preview.startDate}</b> (내일) · 시작월 청구 구독 {preview.billableLines}건 · 안내 받는 번호 {preview.phoneMasked ?? '없음'} · 브랜드 {preview.brand}
          </div>
          {preview.blocking.length > 0 && (
            <ul className="text-red-700 list-disc pl-5">
              {preview.blocking.map((p) => <li key={p}>{p}</li>)}
            </ul>
          )}
          {preview.noticeProblems.length > 0 && (
            <div>
              <ul className="text-amber-800 list-disc pl-5">
                {preview.noticeProblems.map((p) => <li key={p}>{p}</li>)}
              </ul>
              <label className="mt-1 flex items-center gap-1 text-amber-900">
                <input type="checkbox" checked={skipNotice} onChange={(e) => setSkipNotice(e.target.checked)} />
                안내 문자 없이 시작일만 고정 (안내는 담당자가 따로 한다)
              </label>
            </div>
          )}
          <details>
            <summary className="cursor-pointer text-gray-600">보낼 문구 보기 — {preview.subject}</summary>
            <pre className="mt-1 whitespace-pre-wrap text-xs bg-white border rounded p-2">{preview.text}</pre>
          </details>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1 rounded text-sm bg-indigo-600 text-white disabled:opacity-50"
              onClick={start}
              disabled={!canStart || busy}
            >
              {skipNotice ? '안내 없이 과금 시작' : '과금 시작 + 안내 문자 발송'}
            </button>
            <span className="text-xs text-gray-500">🔴 시작일은 되돌릴 수 없고, 문자는 실제 고객에게 나갑니다.</span>
          </div>
        </div>
      )}
    </div>
  );
}
